import { useState } from 'react';
import { ApiClientError } from '../../api/client';
import {
  PLATFORM_LABELS,
  useCreateProfile,
  useUpdateProfile,
  type Platform,
  type StreamingProfile,
} from '../../api/streaming-profiles';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';

const RTMP_URL_PATTERN = /^rtmps?:\/\/[\w.-]+(:\d+)?(\/.*)?$/;

type Props =
  | {
      mode: 'create';
      onClose: () => void;
    }
  | {
      mode: 'edit';
      profile: StreamingProfile;
      onClose: () => void;
    };

export function ProfileForm(props: Props) {
  const initial = props.mode === 'edit' ? props.profile : null;
  const [name, setName] = useState(initial?.name ?? '');
  const [platform, setPlatform] = useState<Platform>(initial?.platform ?? 'fc2_live');
  const [rtmpUrl, setRtmpUrl] = useState(initial?.rtmp_url ?? '');
  const [streamKey, setStreamKey] = useState('');
  const [isDefault, setIsDefault] = useState(initial?.is_default ?? false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useCreateProfile();
  const updateMutation = useUpdateProfile(initial?.ulid ?? '');
  const submitting = createMutation.isPending || updateMutation.isPending;
  const apiError =
    props.mode === 'create' ? createMutation.error : updateMutation.error;

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = '名前を入力してください';
    if (!rtmpUrl.trim()) next.rtmp_url = 'RTMP URL を入力してください';
    else if (!RTMP_URL_PATTERN.test(rtmpUrl.trim())) {
      next.rtmp_url = 'rtmp:// または rtmps:// で始まる URL を指定してください';
    }
    if (props.mode === 'create' && !streamKey.trim()) {
      next.stream_key = 'ストリームキーを入力してください';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    try {
      if (props.mode === 'create') {
        await createMutation.mutateAsync({
          name: name.trim(),
          platform,
          rtmp_url: rtmpUrl.trim(),
          stream_key: streamKey,
          is_default: isDefault,
        });
      } else {
        const patch: {
          name?: string;
          platform?: Platform;
          rtmp_url?: string;
          stream_key?: string;
        } = {};
        if (name.trim() !== initial!.name) patch.name = name.trim();
        if (platform !== initial!.platform) patch.platform = platform;
        if (rtmpUrl.trim() !== initial!.rtmp_url) patch.rtmp_url = rtmpUrl.trim();
        if (streamKey) patch.stream_key = streamKey;
        if (Object.keys(patch).length === 0) {
          props.onClose();
          return;
        }
        await updateMutation.mutateAsync(patch);
      }
      props.onClose();
    } catch {
      // エラーは下のメッセージで表示
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Input
        label="名前"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={errors.name}
        maxLength={100}
        placeholder="例: FC2 ライブ(メイン)"
      />
      <Select
        label="プラットフォーム"
        value={platform}
        onChange={(e) => setPlatform(e.target.value as Platform)}
      >
        {(Object.keys(PLATFORM_LABELS) as Platform[]).map((p) => (
          <option key={p} value={p}>
            {PLATFORM_LABELS[p]}
          </option>
        ))}
      </Select>
      <Input
        label="RTMP URL"
        value={rtmpUrl}
        onChange={(e) => setRtmpUrl(e.target.value)}
        error={errors.rtmp_url}
        maxLength={500}
        placeholder="rtmp://example.com/live"
      />
      <Input
        label={props.mode === 'edit' ? 'ストリームキー(変更する場合のみ)' : 'ストリームキー'}
        type="password"
        value={streamKey}
        onChange={(e) => setStreamKey(e.target.value)}
        error={errors.stream_key}
        maxLength={200}
        placeholder={props.mode === 'edit' ? '空欄なら変更しません' : ''}
        autoComplete="off"
      />
      {props.mode === 'create' && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            margin: '0 0 16px',
            fontSize: '12px',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          このプロファイルをデフォルトに設定する
        </label>
      )}

      {apiError && (
        <p
          style={{
            margin: '0 0 12px',
            fontSize: '11px',
            color: 'var(--color-error-text)',
          }}
        >
          {apiError instanceof ApiClientError ? apiError.message : '保存に失敗しました'}
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <div style={{ minWidth: 100 }}>
          <Button
            type="button"
            variant="secondary"
            onClick={props.onClose}
            disabled={submitting}
          >
            キャンセル
          </Button>
        </div>
        <div style={{ minWidth: 120 }}>
          <Button type="submit" disabled={submitting}>
            {submitting ? '保存中…' : props.mode === 'edit' ? '更新' : '作成'}
          </Button>
        </div>
      </div>
    </form>
  );
}
